import {useMemo} from 'react';

import {randomString} from '../../../../../../libs/shared/helpers/src/utilities/stringHelpers';
import {useConstant} from '../../../../../../srcCommon/helpers/hookHelpers';
import {useRuleEditorContext} from '../../../../editors/ruleEditor/ruleEditorContext';
import {createActionWithParamsAndClientId} from '../../../ruleActionHelpers';
import {RuleAction} from '../../../ruleActionModel';
import {createConditionWithParamsAndClientId} from '../../../ruleConditionHelpers';
import {RuleCondition} from '../../../ruleConditionModel';

function useStableClientId() {
  return useConstant(() => randomString(8));
}

export function useRuleActionWithParams<TAction extends RuleAction>(
  actionType: TAction['type'],
  params: TAction['params'],
) {
  const stableClientId = useStableClientId();
  const {ruleIntegration} = useRuleEditorContext();

  const actionDefinition = ruleIntegration.findActionDefinition({
    integration_id: 0,
    type: actionType,
  });

  if (!actionDefinition) {
    throw new Error(`Did not find a '${actionType}' action`);
  }

  return useMemo(
    () => createActionWithParamsAndClientId<TAction>(actionDefinition, params, stableClientId),
    [actionDefinition, params, stableClientId],
  );
}

export function useRuleConditionWithParams<TCondition extends RuleCondition>(
  conditionType: TCondition['type'],
  params: TCondition['params'],
) {
  const stableClientId = useStableClientId();
  const {ruleIntegration} = useRuleEditorContext();

  const conditionDefinition = ruleIntegration.findConditionDefinition({
    integration_id: 0,
    type: conditionType,
  });

  if (!conditionDefinition) {
    throw new Error(`Did not find a '${conditionType}' condition`);
  }

  return useMemo(
    () => createConditionWithParamsAndClientId<TCondition>(conditionDefinition, params, stableClientId),
    [conditionDefinition, params, stableClientId],
  );
}
